// src/components/DashboardLayout.jsx

import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import {
  Home,
  LogOut,
  Users,
  KeyRound,
  HelpCircle,
  Bug,
  BellMinus,
  Warehouse,
  SlidersHorizontal,
  PackageOpen,
  PackagePlus,
  CheckCircle,
  Network,
  Archive,
  Send,
  Info,
  Database,
  FileBox,
  BookText,
  Eye,
  Coins,
  CalendarDays,
  CalendarCheck,
  Boxes,
  ClockFading,
  GraduationCap,
  ClipboardList,
  ChartColumnDecreasing,
  FileUp,
  FileSpreadsheet,
  TriangleAlert,
  CirclePercent,
  User as UserIcon,
  CheckSquare,
  Loader2,
} from "lucide-react";

import TasksBadge from "./TasksBadge";
import ExportBadge from "./ExportBadge";
import lemonLogo from "../assets/lemon.png";

/* ============================================================
   TOPBAR
   ============================================================ */
const Topbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  const [menuOpen, setMenuOpen] = useState(false);

  const username = user?.username || "";
  const firma = user?.firma || "";
  const fullName = user?.fullName || "";
  const role = user?.role || "";

  const title =
    firma && fullName
      ? `Lékárna ${firma}: ${fullName}`
      : firma
      ? `Lékárna ${firma}`
      : "Lékárna";

  // verze aplikace (zobrazí se jen pokud je definovaná v .env)
  const appVersion =
    (typeof process !== "undefined" &&
      process.env &&
      process.env.REACT_APP_VERSION) ||
    "";

  const profilePath = role === "admin" ? "/admin/profile" : "/profile";

  // tvůj support email natvrdo
  const SUPPORT_EMAIL = "jan-juchelka@seznam.cz";

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const itemBase =
    "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-blue-950 hover:bg-blue-50 transition-colors";
  const itemActive = "bg-blue-600 text-white hover:bg-blue-600";

  const go = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const reportProblem = () => {
    const url = window.location.href;

    const body = [
      "Popis problému:",
      "",
      "----",
      `Firma: ${firma || "-"}`,
      `Role: ${role || "-"}`,
      `Pobočka: ${user?.pharmacyCode || "-"}`,
      `Uživatel: ${username || "-"}`,
      `URL: ${url}`,
      `Čas: ${new Date().toLocaleString("cs-CZ")}`,
    ].join("\n");

    const subject = `Lékárnet – nahlášení problému (${firma || "firma"} / ${
      user?.pharmacyCode || "-"
    })`;

    const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    window.location.href = mailto;
    setMenuOpen(false);
  };

  return (
    <div className="sticky top-0 z-10 -mx-8 mb-6 px-8 py-4 bg-white border-b border-blue-100 flex items-center gap-4 backdrop-blur-md">
      <h1 className="text-2xl md:text-3xl font-black text-blue-900">
        {title}
      </h1>

      <div
        className="ml-auto flex items-center gap-3 relative"
        ref={menuRef}
      >
        {firma === "LEMON" && (
          <img
            src={lemonLogo}
            alt="Lékárnet"
            className="h-8 w-auto object-contain"
          />
        )}

        {/* User badge */}
        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-3 py-2 hover:bg-blue-50 transition-colors"
        >
          <div className="h-6 w-6 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500" />
          <span className="text-sm font-medium text-blue-900">
            {username}
          </span>
        </button>

        {menuOpen && (
          <div className="absolute right-0 top-14 w-64 bg-white border border-blue-200 rounded-xl shadow-lg p-2 z-20">
            <button
              type="button"
              onClick={() => go(profilePath)}
              className={`${itemBase} ${
                location.pathname === profilePath ? itemActive : ""
              }`}
            >
              <UserIcon size={18} />
              <span>Profil</span>
            </button>

            <button
              type="button"
              onClick={() => go("/change-password")}
              className={itemBase}
            >
              <KeyRound size={18} />
              <span>Změna hesla</span>
            </button>

            <button
              type="button"
              onClick={() => go("/help")}
              className={itemBase}
            >
              <HelpCircle size={18} />
              <span>Nápověda</span>
            </button>

            <button
              type="button"
              onClick={reportProblem}
              className={itemBase}
            >
              <Bug size={18} />
              <span>Nahlásit problém</span>
            </button>

            <div className="my-2 border-t border-blue-100" />

            <button
              type="button"
              onClick={() => {
                logout();
                navigate("/login", { replace: true });
              }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-700 hover:bg-red-50 transition-colors"
            >
              <LogOut size={18} />
              <span>Odhlásit se</span>
            </button>

            {appVersion && (
              <div className="mt-2 px-3 py-1 text-xs text-gray-400">
                verze: {appVersion}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ============================================================
   ITEM
   ============================================================ */
function NavItem({ to, icon: Icon, label, rightSlot = null, small = false }) {
  const { pathname } = useLocation();
  const active =
    pathname === to ||
    pathname.startsWith(to + "/") ||
    pathname.startsWith(to + "?");

  return (
    <Link
      to={to}
      className={`group flex items-center gap-3 rounded-xl px-3 py-2 transition-all ${
        active
          ? "bg-white/15 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.2)]"
          : "hover:bg-white/10"
      } ${small ? "text-sm ml-6" : ""}`}
    >
      {Icon && <Icon size={small ? 16 : 20} className="opacity-90" />}
      <span className="font-medium">{label}</span>
      {rightSlot && <span className="ml-auto">{rightSlot}</span>}
    </Link>
  );
}

function ReminderBadge({ count = 0 }) {
  if (!count) return null;

  return (
    <span className="inline-flex items-center justify-center rounded-full text-[10px] font-bold px-1.5 h-4 min-w-4 bg-amber-500 text-white align-middle">
      {count > 99 ? "99+" : count}
    </span>
  );
}

/* ============================================================
   GROUP
   ============================================================ */
function NavGroup({ icon: Icon, label, children, childPaths = [] }) {
  const { pathname } = useLocation();
  const hasActive = childPaths.some((p) => pathname.startsWith(p));
  const [open, setOpen] = useState(hasActive);

  useEffect(() => {
    if (hasActive) setOpen(true);
  }, [hasActive]);

  return (
    <div>
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center gap-3 rounded-xl px-3 py-2 hover:bg-white/10"
      >
        {Icon && <Icon size={20} className="opacity-90" />}
        <span className="font-medium flex-1 text-left">{label}</span>
        <span className="text-xs opacity-70">{open ? "▲" : "▼"}</span>
      </button>

      {open && <div className="mt-1 space-y-1">{children}</div>}
    </div>
  );
}

/* ============================================================
   SIDEBAR FRAME
   ============================================================ */
function SidebarFrame({ children }) {
  const navigate = useNavigate();
  const { logout } = useAuth();

  return (
    <aside className="w-72 min-h-screen p-6 text-white bg-gradient-to-b from-blue-700 via-blue-800 to-indigo-900 shadow-xl">
      <h2 className="text-2xl font-extrabold mb-10">Lékárnet</h2>

      <nav className="space-y-2 pb-20">
        {children}
        <button
          onClick={() => {
            logout();
            navigate("/login", { replace: true });
          }}
          className="mt-6 flex items-center gap-3 rounded-xl px-3 py-2 bg-white/10 hover:bg-white/20"
        >
          <LogOut size={20} />
          <span className="font-medium">Odhlásit se</span>
        </button>
      </nav>
    </aside>
  );
}

/* ============================================================
   GLOBAL REMINDERS
   ============================================================ */
function useGlobalReminders(user) {
  const navigate = useNavigate();

  const [reminderTasks, setReminderTasks] = useState([]);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [activeReminderCount, setActiveReminderCount] = useState(0);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const reminderKey = user?.pharmacyCode
    ? String(user.pharmacyCode).trim()
    : user?._id
    ? String(user._id)
    : null;

  const tasksPath =
    user?.role === "admin" || user?.role === "rm" ? "/admin/tasks" : "/tasks";

  const isDoneForCurrentTarget = (t) => {
    if (!reminderKey) return false;
    return !!t?.completions?.[reminderKey]?.done;
  };

  const isReminderActive = (t) => {
    if (!t?.remindAt) return false;
    if (t?.status === "done" || t?.status === "archived") return false;
    if (isDoneForCurrentTarget(t)) return false;

    const remindAt = new Date(t.remindAt);
    if (isNaN(remindAt.getTime())) return false;

    const snoozeUntil = reminderKey ? t?.snoozes?.[reminderKey]?.until : null;
    if (snoozeUntil) {
      const snoozeDate = new Date(snoozeUntil);
      if (!isNaN(snoozeDate.getTime()) && snoozeDate > new Date()) {
        return false;
      }
    }

    return remindAt <= new Date();
  };

  const refreshReminders = async ({ autoOpen = true } = {}) => {
    if (!user) return;

    try {
      const res = await fetch("/api/tasks/my", {
        method: "GET",
        credentials: "include",
      });

      const data = await res.json().catch(() => []);
      if (!res.ok) return;

      const active = (Array.isArray(data) ? data : []).filter(isReminderActive);

      setReminderTasks(active);
      setActiveReminderCount(active.length);

      if (autoOpen && active.length > 0) {
        setReminderOpen(true);
      }

      if (active.length === 0) {
        setReminderOpen(false);
      }
    } catch (err) {
      console.error("Reminder check failed:", err);
    }
  };

  useEffect(() => {
    if (!user) return;

    refreshReminders({ autoOpen: true });

    const id = setInterval(() => {
      refreshReminders({ autoOpen: true });
    }, 60000);

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, user?.pharmacyCode, user?.role]);

  const applySnooze = async (task, untilIso) => {
    try {
      setActionLoadingId(task._id);

      const res = await fetch(`/api/tasks/${task._id}/snooze`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ until: untilIso }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      await refreshReminders({ autoOpen: false });
    } catch (err) {
      console.error("Snooze failed:", err);
    } finally {
      setActionLoadingId(null);
    }
  };

  const snoozeTask = async (task, amount, unit = "days") => {
    const baseSource =
      (reminderKey && task?.snoozes?.[reminderKey]?.until) ||
      task?.remindAt ||
      new Date().toISOString();

    const base = new Date(baseSource);
    if (isNaN(base.getTime())) return;

    if (unit === "minutes") {
      base.setMinutes(base.getMinutes() + amount);
    } else {
      base.setDate(base.getDate() + amount);
    }

    await applySnooze(task, base.toISOString());
  };

  const completeTask = async (task) => {
    try {
      setActionLoadingId(task._id);

      const res = await fetch(`/api/tasks/${task._id}/complete`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ done: true }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      await refreshReminders({ autoOpen: false });
    } catch (err) {
      console.error("Complete reminder failed:", err);
    } finally {
      setActionLoadingId(null);
    }
  };

  const openTasks = () => {
    setReminderOpen(false);
    navigate(tasksPath);
  };

  const ReminderModal = () => {
    if (!reminderOpen) return null;

    return (
      <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-2xl rounded-xl bg-white shadow-lg">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h3 className="text-lg font-semibold">Aktivní připomenutí</h3>
            <button
              onClick={() => setReminderOpen(false)}
              className="text-sm underline"
            >
              zavřít
            </button>
          </div>

          <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
            {reminderTasks.map((t) => (
              <div
                key={t._id}
                className="border rounded-xl p-4 bg-blue-50/40"
              >
                <div className="font-medium mb-1">🔔 {t.title}</div>

                {t.description && (
                  <div className="text-sm text-gray-700 mb-2">
                    {t.description}
                  </div>
                )}

                <div className="text-xs text-gray-600 mb-3">
                  Termín splnění:{" "}
                  {t.dueDate
                    ? new Date(t.dueDate).toLocaleDateString("cs-CZ")
                    : "bez termínu"}
                  {t.remindAt && (
                    <>
                      {" "}
                      · Připomenutí:{" "}
                      {new Date(t.remindAt)
                        .toLocaleString("cs-CZ")
                        .replace(/\. /g, ".")}
                    </>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => completeTask(t)}
                    disabled={actionLoadingId === t._id}
                    className="px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 text-sm disabled:opacity-60"
                  >
                    Hotovo
                  </button>

                  <button
                    onClick={() => snoozeTask(t, 15, "minutes")}
                    disabled={actionLoadingId === t._id}
                    className="px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 disabled:opacity-60"
                  >
                    +15 min
                  </button>

                  <button
                    onClick={() => snoozeTask(t, 1, "days")}
                    disabled={actionLoadingId === t._id}
                    className="px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 disabled:opacity-60"
                  >
                    +1 den
                  </button>

                  <button
                    onClick={() => snoozeTask(t, 7, "days")}
                    disabled={actionLoadingId === t._id}
                    className="px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 disabled:opacity-60"
                  >
                    +7 dní
                  </button>

                  <button
                    onClick={openTasks}
                    className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm"
                  >
                    Otevřít úkoly
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return {
    activeReminderCount,
    ReminderModal,
  };
}

/* ============================================================
   USER + RM DASHBOARD
   ============================================================ */
export function UserDashboardLayout({ children }) {
  const { user, authLoading } = useAuth();
  const { activeReminderCount, ReminderModal } = useGlobalReminders(user);

  if (authLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-600">
        <Loader2 className="animate-spin" size={18} />
        Načítám přihlášení…
      </div>
    );
  }

  const role = user?.role;

  // admin sem nesmí – zabrání “přepnutí menu” do user režimu
  if (role === "admin") {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Přístup mají user + rm
  if (role !== "user" && role !== "rm") return null;

  const dashboardPath = role === "rm" ? "/rm/dashboard" : "/user/dashboard";

  return (
    <div className="flex min-h-screen">
      <SidebarFrame>
        <NavItem to={dashboardPath} icon={Home} label="Dashboard" />
        <NavItem to="/calendar" icon={CalendarDays} label="Pobočkový kalendář" />

        {/* Úkoly – jen user (ty máš /tasks jen pro user) */}
        {role === "user" && (
          <NavItem
            to="/tasks"
            icon={CheckSquare}
            label="Úkoly"
            rightSlot={
              <div className="flex items-center gap-1">
                <ReminderBadge count={activeReminderCount} />
                <TasksBadge />
              </div>
            }
          />
        )}

        {/* PERSONÁL */}
        {/* VOLNO */}
        <NavGroup
          icon={CalendarDays}
          label="Volno"
          childPaths={["/personal/leaves", "/personal/leaves-overview"]}
        >
          <NavItem to="/personal/leaves" icon={ClipboardList} label="Žádost o volno" small />
          <NavItem
            to="/personal/leaves-overview"
            icon={FileBox}
            label="Žádosti o volno – přehled"
            small
          />
        </NavGroup>

        {/* ROZPISY SMĚN */}
        <NavGroup
          icon={CalendarCheck}
          label="Rozpisy směn"
          childPaths={["/shifts", "/personal/shifts-history"]}
        >
          <NavItem to="/shifts" icon={CalendarCheck} label="Plánování směn" small />
          {role === "user" && (
            <NavItem
              to="/personal/shifts-history"
              icon={BookText}
              label="Historie rozpisů"
              small
            />
          )}
        </NavGroup>
        <NavGroup
          icon={GraduationCap}
          label="Vzdělávání"
          childPaths={["/training"]}
        >
          <NavItem
            to="/training"
            icon={GraduationCap}
            label="Plnění školení"
            small
          />
        </NavGroup>
        <NavItem to="/upload" icon={FileUp} label="Nahrát exporty" rightSlot={<ExportBadge />} />
        <NavItem to="/pharmacy/report" icon={ChartColumnDecreasing} label="Očekávané změny úhrad" />
        <NavItem to="/orders" icon={ClipboardList} label="Návrh objednávky" />

        <NavItem to="/pharmacy/ohrozenci" icon={TriangleAlert} label="Ohroženci" />
        <NavItem to="/pharmacy/lezaky" icon={ClockFading} label="Ležáky" />
        <NavItem to="/overstock" icon={Warehouse} label="Nadzásoby" />
        <NavItem to="/remote-stocks" icon={Network} label="Vzdálené sklady" />
        <NavItem to="/orders/leaflet" icon={CirclePercent} label="Objednávka z letáku" />
        <NavItem to="/discounted" icon={BellMinus} label="Expirační slevy" />
        <NavItem to="/norm-mismatch" icon={SlidersHorizontal} label="Nesoulad norem" />
        <NavItem to="/watchlists" icon={Eye} label="Watchlisty" />

        {/* Převodky – příjem */}
        <NavGroup
          icon={PackageOpen}
          label="Převodky – příjem"
          childPaths={[
            "/transfers/incoming",
            "/transfers/confirmed",
            "/transfers/received",
            "/transfers/archive",
          ]}
        >
          <NavItem to="/transfers/incoming" icon={ClipboardList} label="Návrhy k přijetí" small />
          <NavItem to="/transfers/confirmed" icon={CheckCircle} label="Potvrzené" small />
          <NavItem to="/transfers/received" icon={FileBox} label="Přijaté" small />
          <NavItem to="/transfers/archive" icon={Archive} label="Archiv" small />
        </NavGroup>

        {/* Převodky – výdej */}
        <NavGroup
          icon={PackagePlus}
          label="Převodky – výdej"
          childPaths={[
            "/transfers/outgoing",
            "/transfers/prepared",
            "/transfers/shipped",
          ]}
        >
          <NavItem to="/transfers/outgoing" icon={ClipboardList} label="K odeslání" small />
          <NavItem to="/transfers/prepared" icon={CheckCircle} label="Vychystané" small />
          <NavItem to="/transfers/shipped" icon={Send} label="Odeslané" small />
        </NavGroup>

        <NavItem to="/pool" icon={Boxes} label="Pool přípravků" />
        <NavItem to="/ledger" icon={BookText} label="Účetní kniha" />

        {/* Nastavení */}
        <div className="pt-4">
          <p className="text-xs uppercase tracking-wider text-blue-200/80 mb-2">
            Nastavení
          </p>
          <NavItem to="/profile" icon={UserIcon} label="Profil" />
          <NavItem to="/employees" icon={Users} label="Zaměstnanci" />
        </div>
      </SidebarFrame>

      <main className="flex-1 bg-white p-8">
        <Topbar />
        <div className="mt-8">{children}</div>
        <ReminderModal />
      </main>
    </div>
  );
}

/* ============================================================
   ADMIN + RM DASHBOARD (RM má omezené menu)
   ============================================================ */
export function AdminDashboardLayout({ children }) {
  const { user, authLoading } = useAuth();
  const { activeReminderCount, ReminderModal } = useGlobalReminders(user);

  if (authLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-600">
        <Loader2 className="animate-spin" size={18} />
        Načítám přihlášení…
      </div>
    );
  }

  const role = user?.role;

  // pustíme admin i rm (RM má svoje schvalovací routy právě přes AdminDashboardLayout)
  if (role !== "admin" && role !== "rm") return null;

  const isRM = role === "rm";

  return (
    <div className="flex min-h-screen">
      <SidebarFrame>
        <NavItem
          to="/announcements"
          icon={BookText}
          label="Nástěnka (příspěvky)"
        />

        {/* PERSONÁL */}
        {/* ZAMĚSTNANCI (admin-only) */}
        {!isRM && (
          <NavGroup
            icon={Users}
            label="Zaměstnanci"
            childPaths={["/admin/personal/employees"]}
          >
            <NavItem
              to="/admin/personal/employees"
              icon={UserIcon}
              label="Seznam zaměstnanců"
              small
            />
          </NavGroup>
        )}

        {/* VOLNO */}
        <NavGroup
          icon={CalendarDays}
          label="Volno"
          childPaths={[
            "/admin/personal/approve",
            "/admin/personal/leaves-overview",
            "/admin/personal/leaves",
          ]}
        >
          <NavItem
            to="/admin/personal/approve"
            icon={CheckCircle}
            label="Schvalování volna"
            small
          />
          <NavItem
            to="/admin/personal/leaves-overview"
            icon={FileBox}
            label="Žádosti o volno – přehled"
            small
          />
          <NavItem
            to="/admin/personal/leaves"
            icon={ClipboardList}
            label="Žádost o volno"
            small
          />
        </NavGroup>

        {/* ROZPISY SMĚN */}
        <NavGroup
          icon={CalendarCheck}
          label="Rozpisy směn"
          childPaths={[
            "/admin/personal/shifts-overview",
            "/admin/personal/approve/shifts",
            "/admin/personal/shifts-history",
            "/admin/shifts",
          ]}
        >
          <NavItem
            to="/admin/personal/shifts-overview"
            icon={CalendarCheck}
            label="Dohled směn"
            small
          />

          <NavItem
            to="/admin/personal/approve/shifts"
            icon={CalendarCheck}
            label="Schvalování rozpisů"
            small
          />

          <NavItem
            to="/admin/personal/shifts-history"
            icon={Archive}
            label="Historie rozpisů"
            small
          />

          {!isRM && (
            <NavItem
              to="/admin/shifts"
              icon={CalendarCheck}
              label="Plánování směn"
              small
            />
          )}
        </NavGroup>
        <NavGroup
          icon={GraduationCap}
          label="Vzdělávání"
          childPaths={["/training", "/admin/personal/training-definitions"]}
        >
          <NavItem
            to="/training"
            icon={GraduationCap}
            label="Přehled plnění"
            small
          />
          <NavItem
            to="/admin/personal/training-definitions"
            icon={ClipboardList}
            label="Správa školení"
            small
          />
        </NavGroup>
        {/* Úkoly (správa) – máš povoleno i pro rm v App.js */}
        <NavItem
          to="/admin/tasks"
          icon={CheckSquare}
          label={isRM ? "Úkoly" : "Úkoly (správa)"}
          rightSlot={
            <div className="flex items-center gap-1">
              <ReminderBadge count={activeReminderCount} />
              <TasksBadge />
            </div>
          }
        />

        {/* Zbytek admin-only věcí schováme pro RM */}
        {!isRM && (
          <>
            <NavItem
              to="/admin/pharmacy/report"
              icon={ChartColumnDecreasing}
              label="Očekávané změny úhrad"
            />

            <NavItem
              to="/admin/leaflet-upload"
              icon={FileSpreadsheet}
              label="Nahrát akční leták"
            />

            <NavItem
              to="/admin/uhrady-upload"
              icon={Coins}
              label="Nahrát přehled úhrad"
            />

            <NavItem to="/admin/pool-items" icon={Boxes} label="Pool přípravků" />
            <NavItem to="/pool-stats" icon={Info} label="Statistiky poolu" />
            <NavItem to="/data-explorer" icon={Database} label="Data explorer" />

            <NavItem to="/ledger-admin" icon={BookText} label="Účetní kniha" />

            <div className="pt-4">
              <p className="text-xs uppercase tracking-wider text-blue-200/80 mb-2">
                Nastavení
              </p>
              <NavItem to="/admin/profile" icon={UserIcon} label="Profil" />
              <NavItem to="/admin/users" icon={Users} label="Uživatelé" />
            </div>
          </>
        )}

        {/* RM nastavení */}
        {isRM && (
          <div className="pt-4">
            <p className="text-xs uppercase tracking-wider text-blue-200/80 mb-2">
              Nastavení
            </p>
            <NavItem to="/profile" icon={UserIcon} label="Profil" />
          </div>
        )}
      </SidebarFrame>

      <main className="flex-1 bg-white p-8">
        <Topbar />
        <div className="mt-8">{children}</div>
        <ReminderModal />
      </main>
    </div>
  );
}
// ✅ Layout, který vybere sidebar podle role
export function RoleDashboardLayout({ children }) {
  const { user, authLoading } = useAuth();

  if (authLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-600">
        <Loader2 className="animate-spin" size={18} />
        Načítám přihlášení…
      </div>
    );
  }

  const role = user?.role;

  if (role === "admin" || role === "rm") {
    return <AdminDashboardLayout>{children}</AdminDashboardLayout>;
  }

  return <UserDashboardLayout>{children}</UserDashboardLayout>;
}

export default AdminDashboardLayout;